import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import OtherItemImage from "../../assets/images/OtherItem.png";

const ThumbnailContainer = styled.div`
  width: ${(props) => props.$width || "120px"};
  height: ${(props) => props.$height || "120px"};
  background-color: ${(props) => props.$backgroundColor || "#ffffff"};
  border-radius: ${(props) =>
    props.$borderRadiusTopLeft ||
    props.$borderRadiusTopRight ||
    props.$borderRadiusBottomLeft ||
    props.$borderRadiusBottomRight
      ? `${props.$borderRadiusTopLeft || "0"} ${
          props.$borderRadiusTopRight || "0"
        } ${props.$borderRadiusBottomRight || "0"} ${
          props.$borderRadiusBottomLeft || "0"
        }`
      : props.$borderRadius || "12px"};
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border: ${(props) => props.$border || "none"};
  box-shadow: ${(props) => props.$boxShadow || "none"};
  position: relative;
  flex-shrink: 0;
  ${(props) => props.$customStyles}
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.$objectFit || "contain"};
  border-radius: inherit;
  transition: transform 0.3s ease;
  user-select: none;

  &:hover {
    transform: ${(props) => (props.$hoverEffect ? "scale(1.05)" : "none")};
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #999;
  font-size: 14px;
  border-radius: inherit;
`;

const Badge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  z-index: 1;
`;

const ItemThumbnail = ({
  src,
  alt,
  width,
  height,
  objectFit,
  borderRadius,
  borderRadiusTopLeft,
  borderRadiusTopRight,
  borderRadiusBottomLeft,
  borderRadiusBottomRight,
  backgroundColor,
  border,
  boxShadow,
  placeholderText,
  hoverEffect,
  badgeContent,
  customStyles,
  className,
  onClick,
  placeholderIcon,
  overlayContent,
  defaultImage = OtherItemImage,
}) => {
  const imageSrc = src || defaultImage;
  const showPlaceholder = !src && (placeholderText || placeholderIcon);

  return (
    <ThumbnailContainer
      $width={width}
      $height={height}
      $borderRadius={borderRadius}
      $borderRadiusTopLeft={borderRadiusTopLeft}
      $borderRadiusTopRight={borderRadiusTopRight}
      $borderRadiusBottomLeft={borderRadiusBottomLeft}
      $borderRadiusBottomRight={borderRadiusBottomRight}
      $backgroundColor={backgroundColor}
      $border={border}
      $boxShadow={boxShadow}
      $customStyles={customStyles}
      className={className}
      onClick={onClick}
      role="img"
      aria-label={alt || "item thumbnail"}
    >
      {showPlaceholder ? (
        <Placeholder>
          {placeholderIcon || placeholderText || "No Image"}
        </Placeholder>
      ) : (
        <>
          <Image
            src={imageSrc}
            alt={alt || "thumbnail"}
            $objectFit={objectFit}
            $hoverEffect={hoverEffect}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          {badgeContent && <Badge>{badgeContent}</Badge>}
          {overlayContent && overlayContent}
        </>
      )}
    </ThumbnailContainer>
  );
};

ItemThumbnail.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  objectFit: PropTypes.oneOf([
    "contain",
    "cover",
    "fill",
    "none",
    "scale-down",
  ]),
  borderRadius: PropTypes.string,
  borderRadiusTopLeft: PropTypes.string,
  borderRadiusTopRight: PropTypes.string,
  borderRadiusBottomLeft: PropTypes.string,
  borderRadiusBottomRight: PropTypes.string,
  backgroundColor: PropTypes.string,
  border: PropTypes.string,
  boxShadow: PropTypes.string,
  placeholderText: PropTypes.string,
  placeholderIcon: PropTypes.node,
  hoverEffect: PropTypes.bool,
  badgeContent: PropTypes.node,
  overlayContent: PropTypes.node,
  customStyles: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

ItemThumbnail.defaultProps = {
  objectFit: "contain",
  hoverEffect: false,
};

export default ItemThumbnail;
